/* Origin price comparison — product page control.
 *
 * This element never owns variant state. It drives whichever variant engine the
 * page already has, so price, SKU, stock, image and add-to-cart keep updating
 * through the theme's own code path:
 *
 *   engine "theme"       -> sections/product-template-1.liquid
 *                           ticks .single-option-selector-{sectionId} and fires
 *                           a native change, which assets/theme.js listens for.
 *
 *   engine "advancepage" -> sections/product-advancepage.liquid
 *                           clicks the matching [data-ap-swatch] button, which
 *                           that section's inline script listens for.
 */

/* ---------- Engine adapters ---------------------------------------------- */

const OriginEngines = {
  theme: {
    /* All native selectors for one option position (1-based). */
    nodes(el, index) {
      return Array.from(
        document.querySelectorAll('.single-option-selector-' + el.sectionId)
      ).filter((n) => n.dataset.index === 'option' + index);
    },

    currentValue(el, index) {
      for (const node of this.nodes(el, index)) {
        if (node.tagName === 'SELECT') return node.value;
        if (node.checked) return node.value;
      }
      return null;
    },

    select(el, index, value) {
      this.nodes(el, index).forEach((node) => {
        if (node.tagName === 'SELECT') {
          if (node.value === value) return;
          node.value = value;
          node.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (node.value === value && !node.checked) {
          node.checked = true;
          node.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    },

    /* Re-sync whenever the engine's own controls change. */
    listen(el, onChange) {
      const handler = (event) => {
        const t = event.target;
        if (!t || !t.classList) return;
        if (!t.classList.contains('single-option-selector-' + el.sectionId)) return;
        window.requestAnimationFrame(onChange);
      };
      document.addEventListener('change', handler, true);
      return () => document.removeEventListener('change', handler, true);
    },
  },

  advancepage: {
    root(el) {
      return document.getElementById('shopify-section-' + el.sectionId);
    },

    /* This section uses 0-based data-option-index. */
    nodes(el, index) {
      const root = this.root(el);
      if (!root) return [];
      return Array.from(
        root.querySelectorAll(
          '[data-ap-swatch][data-option-index="' + (index - 1) + '"]'
        )
      );
    },

    currentValue(el, index) {
      const active = this.nodes(el, index).find((n) =>
        n.classList.contains('is-active')
      );
      return active ? active.dataset.value : null;
    },

    select(el, index, value) {
      const target = this.nodes(el, index).find((n) => n.dataset.value === value);
      if (target && !target.classList.contains('is-active')) target.click();
    },

    listen(el, onChange) {
      const root = this.root(el);
      if (!root) return () => {};
      const handler = (event) => {
        if (!event.target.closest('[data-ap-swatch]')) return;
        window.requestAnimationFrame(onChange);
      };
      root.addEventListener('click', handler);
      return () => root.removeEventListener('click', handler);
    },
  },
};

/* ---------- Element ------------------------------------------------------ */

class OriginCompare extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.optionIndex = parseInt(this.dataset.optionIndex, 10);
    this.currency = this.dataset.currency || 'GBP';
    this.engine = OriginEngines[this.dataset.engine] || OriginEngines.theme;

    this.choices = Array.from(this.querySelectorAll('[data-origin-choice]'));
    this.variants = this.parseVariants();

    this.choices.forEach((choice) => {
      choice.addEventListener('click', () => {
        this.engine.select(this, this.optionIndex, choice.dataset.value);
        this.refresh();
      });
    });

    this.refresh = this.refresh.bind(this);
    this.stopListening = this.engine.listen(this, this.refresh);

    this.refresh();
  }

  disconnectedCallback() {
    if (this.stopListening) this.stopListening();
  }

  parseVariants() {
    const tag = this.querySelector('[data-origin-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return [];
    }
  }

  /* The variant for `value` on our option, holding every other option at
     whatever the shopper currently has selected — so we always compare
     like for like across size, colour, etc. */
  findVariant(value) {
    return this.variants.find((variant) => {
      for (let i = 1; i <= 3; i++) {
        const key = 'o' + i;
        if (variant[key] === null || variant[key] === undefined) continue;
        const wanted =
          i === this.optionIndex ? value : this.engine.currentValue(this, i);
        if (wanted === null) continue;
        if (variant[key] !== wanted) return false;
      }
      return true;
    });
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  refresh() {
    const active = this.engine.currentValue(this, this.optionIndex);
    let ukPrice = null;
    let importPrice = null;

    this.choices.forEach((choice) => {
      const value = choice.dataset.value;
      const isActive = value === active;
      choice.classList.toggle('is-active', isActive);
      choice.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const variant = this.findVariant(value);
      if (!variant) return;

      const priceEl = choice.querySelector('[data-origin-price]');
      if (priceEl) priceEl.textContent = variant.pf;

      choice.classList.toggle('is-unavailable', variant.available === false);

      if (choice.classList.contains('origin-compare__choice--uk')) {
        ukPrice = variant.price;
      } else {
        importPrice = variant.price;
      }
    });

    /* Keep "only £x more" honest when another option changes. */
    const note = this.querySelector('[data-origin-note]');
    if (note && ukPrice !== null && importPrice !== null) {
      const template = note.dataset.template || 'Only [amount] more';
      const diff = ukPrice - importPrice;
      if (diff > 0) {
        note.hidden = false;
        note.textContent = template.replace('[amount]', this.formatMoney(diff));
      } else {
        note.hidden = true;
      }
    }
  }
}

if (!customElements.get('origin-compare')) {
  customElements.define('origin-compare', OriginCompare);
}
